import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { adminAPI } from '../lib/api';
import { DollarSign, TrendingUp, CreditCard, Clock, Users, Building } from 'lucide-react';

export default function Revenue() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState(null);

  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const res = await adminAPI.getRevenue();
        setRevenue(res.data);
      } catch (error) {
        console.error('Error fetching revenue:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRevenue();
  }, []);

  if (loading) return <Layout><div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-28" />)}</div></div></Layout>;

  if (!revenue) return <Layout><Card><CardContent className="py-12 text-center"><p className="text-muted-foreground">Unable to load revenue data</p></CardContent></Card></Layout>;

  const stats = [
    { label: 'Total Revenue', value: `₹${revenue.total_revenue?.toLocaleString() || 0}`, icon: DollarSign, color: 'text-secondary', bg: 'bg-secondary/10' },
    { label: 'Membership Revenue', value: `₹${revenue.membership_revenue?.toLocaleString() || 0}`, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Venue Revenue', value: `₹${revenue.venue_revenue?.toLocaleString() || 0}`, icon: Building, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Outstanding Dues', value: `₹${revenue.outstanding_dues?.toLocaleString() || 0}`, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Pending Membership', value: `₹${revenue.pending_membership?.toLocaleString() || 0}`, icon: CreditCard, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { label: 'Pending Venue', value: `₹${revenue.pending_venue?.toLocaleString() || 0}`, icon: TrendingUp, color: 'text-red-500', bg: 'bg-red-500/10' },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="revenue-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">Revenue Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Financial overview of membership and venue fees</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} data-testid={`revenue-stat-${i}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
                      <p className="text-lg sm:text-2xl font-bold font-heading mt-1">{stat.value}</p>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-full ${stat.bg} shrink-0`}>
                      <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Summary */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="font-heading uppercase tracking-tight text-base">Membership Summary</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Memberships</span><span className="font-bold">{revenue.total_memberships}</span></div>
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">Revenue Collected</span><span className="font-bold text-secondary">₹{revenue.membership_revenue?.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">Outstanding Dues</span><span className="font-bold text-amber-500">₹{revenue.outstanding_dues?.toLocaleString()}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading uppercase tracking-tight text-base">Venue Booking Summary</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Bookings</span><span className="font-bold">{revenue.total_bookings}</span></div>
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">Revenue Collected</span><span className="font-bold text-secondary">₹{revenue.venue_revenue?.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">Pending Payments</span><span className="font-bold text-amber-500">₹{revenue.pending_venue?.toLocaleString()}</span></div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Breakdown */}
        {Object.keys(revenue.monthly_breakdown || {}).length > 0 && (
          <Card>
            <CardHeader><CardTitle className="font-heading uppercase tracking-tight text-base">Monthly Revenue</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(revenue.monthly_breakdown).map(([month, amount]) => (
                  <div key={month} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <span className="font-medium text-sm">{month}</span>
                    <span className="font-bold text-secondary">₹{amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
